import { takeLatest, put, fork } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { LoginWithEmail, ValidateToken } from "../../Apis/AuthApiServices";
import { loginWithEmail, setData } from "../Slice/LoginSlice";
import {
  getJwtExpiryDate,
  rememberWsToken,
  setJwtTokenCookie,
  shouldUseServerSessionAuth,
} from "../../utils/authCookie";

const ValidateTokenData = async () => {
  const result = await ValidateToken();
  return result;
};
const storeInLocalStorage = (resultData: any) => {
  localStorage.setItem("LoginUserID", `${resultData.userID}`);
  localStorage.setItem("doctorId", `${resultData.doctorId}`);
  localStorage.setItem("authenticationType", "Standalone");
  localStorage.setItem("themeSelection", `${resultData.theme}`);
  localStorage.setItem("LastLogin", `${resultData.lastLoginDetails}`);
  localStorage.setItem("termsAgree", `${resultData?.euaCheck}`);
};

export function* onLoadLoginWithEmailAsync({ payload }: any) {
  const data = {
    userID: payload.userID,
    password: payload.password,
  };
  try {
    const result: AxiosResponse = yield LoginWithEmail(data);

    if (result?.data?.digitalRxStatusCode === 200) {
      const rows0 = result?.data?.response?.[0] || {};
      const authToken = rows0?.drxAuthToken || "";
      if (shouldUseServerSessionAuth()) {
        // /auth/session/login already validated this JWT with DigitalRx and created the server
        // session -- it returns the same ws token / expiry / session expiry that
        // /auth/session/exchange would, just under different field names. Calling exchange here
        // would re-validate the same JWT with DigitalRx a second time for no new information.
        const wsToken: string = rememberWsToken({
          ws_token: rows0.drxWsToken,
          ws_token_expires_at: rows0.drxWsTokenExpiresAt,
          session_expires_at: result?.data?.session_expires_at,
        });
        if (!wsToken) {
          throw new Error("Unable to create secure server session");
        }
      } else {
        // Size the cookie to the token's REAL expiry (from its `exp` claim), not a fixed
        // 120-minute window. A cookie that outlives the ~15-min JWT let the dead token linger
        // across refreshes and kept 400-ing until manual logout. Fall back to 120 min only if
        // the token has no readable exp.
        const fallback = new Date(new Date().getTime() + 120 * 60 * 1000);
        setJwtTokenCookie(authToken, getJwtExpiryDate(authToken) ?? fallback);
      }
      const validatingToken: AxiosResponse = yield ValidateTokenData();

      const responseData = validatingToken?.data?.response[0];
      if (validatingToken?.data?.digitalRxStatusCode === 200) {
        const isValidToken = responseData?.validToken;
        const isEuaCheck = responseData?.euaCheck;

        if (isValidToken && !isEuaCheck) {
          storeInLocalStorage(responseData);
          yield put(
            setData({
              data: result?.data?.response[0],
              error: false,
              isLoading: false,
            })
          );
          payload.navigate("/patientsearch", { replace: true });
        } else if (isValidToken && isEuaCheck) {
          storeInLocalStorage(responseData);
          payload.navigate("/terms", { replace: true });
          yield put(
            setData({
              data: result?.data?.response[0],
              error: false,
              isLoading: false,
            })
          );
        }
      } else {
        yield put(
          setData({
            error: true,
            isLoading: false,
            message: "Bad request",
          })
        );
      }
    } else if (result?.data?.digitalRxStatusCode === 403) {
      localStorage.setItem("LoginUserID", payload.userID);
      payload.navigate("/change-password", { replace: true });
      yield put(
        setData({
          error: false,
          isLoading: false,
          message: "",
        })
      );
    } else {
      yield put(
        setData({
          error: true,
          isLoading: false,
          message:
            result?.data?.digitalRxStatusMessage ?? "Something went wrong!",
        })
      );
    }
  } catch (error) {
    yield put(
      setData({
        error: true,
        isLoading: false,
        message: "Try Again After Some Time!",
      })
    );
  }
}

function* onLoadLoginWithEmail() {
  yield takeLatest(loginWithEmail.type, onLoadLoginWithEmailAsync);
}

const LoginSaga = [fork(onLoadLoginWithEmail)];

export default LoginSaga;
